import type { FC } from 'react';

import { useCms, useContent, useSetting } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Stack, Text, TextLink } from '@eneco/sparky';

import { getIllustration } from './illustrations';
import type { USPItemContent } from './usp-item-content.types';

/**
 * `USPItem` block for the CMS-agnostic lane, serving **both** providers. A leaf: title, rich-text
 * description, an optional link, and an illustration.
 *
 * The illustration is resolved through this lib's own `getIllustration` (`illustrations.ts`) rather
 * than `@eneco/cms`'s stub — `lib:cms-components` may depend on `lib:sparky`, so it can reach the
 * real components directly.
 *
 * `mobileVariant: 'compact'` is a rendering parameter the parent `LineUp` sets per placement; it
 * lays the item out as a row on small screens instead of a column.
 */
const USPItem: FC = () => {
  const { adapter } = useCms();
  const content = useContent<USPItemContent>();
  const isCompactOnMobile = useSetting('mobileVariant') === 'compact';

  const description = adapter.toRichText(content.description);
  const link = adapter.toLink(content.link);
  const Illustration = getIllustration(content.illustration);

  return (
    <Stack
      gap={isCompactOnMobile ? { initial: '8', md: '6' } : '6'}
      alignY={isCompactOnMobile ? { initial: 'center', md: 'start' } : 'start'}
      direction={isCompactOnMobile ? { initial: 'row', md: 'column' } : 'column'}>
      {Illustration && (
        <Stack.Item>
          <Illustration color="brandGradient" size={{ initial: 'medium', md: 'large' }} />
        </Stack.Item>
      )}
      <Stack gap="3">
        {content.title && (
          <Text weight="bold" size={{ initial: 'BodyM', md: 'BodyL' }}>
            {content.title}
          </Text>
        )}
        {description && (
          <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
            <RichText html={description} />
          </Text>
        )}
        {link && (
          <TextLink emphasis="high" href={link.href} target={link.target}>
            {link.text ?? link.href}
          </TextLink>
        )}
      </Stack>
    </Stack>
  );
};

export default USPItem;
